/**
 * TOUHeatMap - Time of Use Rate Period Visualization
 * 
 * Displays a heat map showing TOU rate periods across hours and months.
 *
 * Expected tariff data structure:
 * - energyweekdayschedule: 12x24 array (months × hours) with period indices
 * - energyweekendschedule: 12x24 array for weekends
 * - energyratestructure: Array of rate periods, each with tiers and rates
 */

import React, { useMemo, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Tooltip, useTheme } from '@mui/material';

// TOU period colors (matching common utility color schemes)
const PERIOD_COLORS = [
    '#4CAF50',  // Period 0: Off-Peak (Green)
    '#FFC107',  // Period 1: Mid-Peak (Yellow/Amber)
    '#F44336',  // Period 2: On-Peak (Red)
    '#9C27B0',  // Period 3: Super On-Peak (Purple)
    '#2196F3',  // Period 4: (Blue)
    '#FF9800',  // Period 5: (Orange)
];

const PERIOD_NAMES = ['Off-Peak', 'Mid-Peak', 'On-Peak', 'Super Peak', 'Period 4', 'Period 5'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * TOUHeatMap Component
 * 
 * @param {Object} props
 * @param {Object} props.tariff - Tariff rate data
 * @param {number} props.height - Optional height in pixels (default 280)
 */
function TOUHeatMap({ tariff, height = 280 }) {
    const [dayType, setDayType] = useState('weekday');
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Extract schedule and rate data
    const { schedule, rates, periodLabels } = useMemo(() => {
        if (!tariff) return { schedule: null, rates: [], periodLabels: [] };

        // Get the appropriate schedule
        const weekdaySchedule = tariff.energyweekdayschedule || tariff.energy_weekday_schedule || [];
        const weekendSchedule = tariff.energyweekendschedule || tariff.energy_weekend_schedule || [];

        const sched = dayType === 'weekend' ? weekendSchedule : weekdaySchedule;

        // Get rate structure
        const rateStructure = tariff.energyratestructure || tariff.energy_rate_structure || [];

        // Build rate lookup - get the first tier rate for each period
        const rateMap = rateStructure.map((period, idx) => {
            if (Array.isArray(period) && period.length > 0) {
                // Period is array of tiers
                return period[0]?.rate || 0;
            }
            return period?.rate || 0;
        });

        // Determine period labels based on rates
        const labels = rateStructure.map((period, idx) => {
            const rate = rateMap[idx];
            if (rate === 0) return 'Off-Peak';
            // Categorize by relative rate
            const maxRate = Math.max(...rateMap.filter(r => r > 0));
            const minRate = Math.min(...rateMap.filter(r => r > 0));
            if (maxRate === minRate) return 'Flat Rate';
            const ratio = (rate - minRate) / (maxRate - minRate);
            if (ratio < 0.33) return 'Off-Peak';
            if (ratio < 0.66) return 'Mid-Peak';
            return 'On-Peak';
        });

        return { schedule: sched, rates: rateMap, periodLabels: labels };
    }, [tariff, dayType]);

    if (!schedule || schedule.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">
                    No TOU schedule data available for this tariff
                </Typography>
            </Box>
        );
    }

    // Normalize schedule - ensure it's 12 months × 24 hours
    const normalizedSchedule = useMemo(() => {
        if (!Array.isArray(schedule)) return [];

        // If schedule is already 12x24, use as-is
        if (schedule.length === 12 && Array.isArray(schedule[0]) && schedule[0].length === 24) {
            return schedule;
        }

        // If it's a flat 24-hour array, replicate for all months
        if (schedule.length === 24 && typeof schedule[0] === 'number') {
            return Array(12).fill(schedule);
        }

        return schedule;
    }, [schedule]);

    const cellWidth = 100 / 24;  // Percentage width per hour
    const cellHeight = (height - 60) / 12;  // Height per month, minus header/footer

    // Get unique periods for legend
    const uniquePeriods = useMemo(() => {
        const periods = new Set();
        normalizedSchedule.forEach(month => {
            if (Array.isArray(month)) {
                month.forEach(p => periods.add(p));
            }
        });
        return Array.from(periods).sort((a, b) => a - b);
    }, [normalizedSchedule]);

    const borderColor = isDark ? 'rgba(255, 255, 255, 0.12)' : '#e0e0e0';
    const labelBg = isDark ? 'rgba(255, 255, 255, 0.05)' : '#fafafa';
    const labelColor = theme.palette.text.secondary;

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header with toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    TOU Rate Periods
                </Typography>
                <ToggleButtonGroup
                    value={dayType}
                    exclusive
                    onChange={(e, val) => val && setDayType(val)}
                    size="small"
                >
                    <ToggleButton value="weekday" sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem' }}>
                        Weekday
                    </ToggleButton>
                    <ToggleButton value="weekend" sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem' }}>
                        Weekend
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Heat Map Grid */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${borderColor}`,
                borderRadius: 1,
                overflow: 'hidden'
            }}>
                {/* Hour labels */}
                <Box sx={{ display: 'flex', borderBottom: `1px solid ${borderColor}`, bgcolor: labelBg }}>
                    <Box sx={{ width: 40, flexShrink: 0 }} /> {/* Month label space */}
                    {Array.from({ length: 24 }, (_, h) => (
                        <Box
                            key={h}
                            sx={{
                                width: `${cellWidth}%`,
                                textAlign: 'center',
                                fontSize: '0.65rem',
                                color: labelColor,
                                py: 0.25
                            }}
                        >
                            {h % 3 === 0 ? h : ''}
                        </Box>
                    ))}
                </Box>

                {/* Month rows */}
                {normalizedSchedule.map((monthSchedule, monthIdx) => (
                    <Box
                        key={monthIdx}
                        sx={{
                            display: 'flex',
                            borderBottom: monthIdx < 11 ? `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f0f0f0'}` : 'none'
                        }}
                    >
                        {/* Month label */}
                        <Box sx={{
                            width: 40,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            color: labelColor,
                            bgcolor: labelBg,
                            borderRight: `1px solid ${borderColor}`
                        }}>
                            {MONTHS[monthIdx]}
                        </Box>

                        {/* Hour cells */}
                        {Array.isArray(monthSchedule) && monthSchedule.map((period, hourIdx) => {
                            const color = PERIOD_COLORS[period] || '#ccc';
                            const rate = rates[period] || 0;
                            const label = periodLabels[period] || PERIOD_NAMES[period] || `Period ${period}`;

                            return (
                                <Tooltip
                                    key={hourIdx}
                                    title={`${MONTHS[monthIdx]} ${hourIdx}:00 - ${label} ($${rate.toFixed(4)}/kWh)`}
                                    arrow
                                    placement="top"
                                >
                                    <Box
                                        sx={{
                                            width: `${cellWidth}%`,
                                            height: cellHeight,
                                            bgcolor: color,
                                            transition: 'opacity 0.15s',
                                            '&:hover': {
                                                opacity: 0.7,
                                                cursor: 'pointer'
                                            }
                                        }}
                                    />
                                </Tooltip>
                            );
                        })}
                    </Box>
                ))}
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                {uniquePeriods.map(period => (
                    <Box key={period} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                            width: 14,
                            height: 14,
                            bgcolor: PERIOD_COLORS[period] || '#ccc',
                            borderRadius: 0.5,
                            border: `1px solid ${borderColor}`
                        }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {periodLabels[period] || PERIOD_NAMES[period] || `Period ${period}`}
                            {rates[period] > 0 && ` ($${rates[period].toFixed(3)})`}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Axis labels */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Hour of Day (0-23)
                </Typography>
            </Box>
        </Box>
    );
}

export default TOUHeatMap;
